import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetNotificationsQuery, useSearchQuery, useGetBasicInfoQuery } from '../redux/apiSlice';

const Navbar = ({ setSidebarOpen, onLogout }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isNotificationsDropdownOpen, setIsNotificationsDropdownOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [dropdownAnimation, setDropdownAnimation] = useState('');
  const [notificationAnimation, setNotificationAnimation] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);
  
  // Use navigate for routing
  const navigate = useNavigate();

  // Basic Info API call
  const { 
    data: basicInfoData, 
    isLoading: basicInfoLoading, 
    error: basicInfoError 
  } = useGetBasicInfoQuery();

  // Notifications API call
  const { 
    data: notificationsData, 
    isLoading: notificationsLoading, 
    error: notificationsError,
    refetch: refetchNotifications 
  } = useGetNotificationsQuery(undefined, {
    pollingInterval: 60000,
  });

  // Search API call
  const { 
    data: searchData, 
    isLoading: searchLoading, 
    error: searchError 
  } = useSearchQuery(searchQuery, {
    skip: !searchQuery || searchQuery.trim() === '',
  });

  // Extract data from API responses
  const basicInfo = basicInfoData?.data || {};
  const notifications = notificationsData?.data || [];
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Get full name from basic info (fname + lname)
  const fullName = basicInfo.fname && basicInfo.lname 
    ? `${basicInfo.fname} ${basicInfo.lname}`
    : 'Dr. Sarah';

  // Get first name for welcome message
  const firstName = basicInfo.fname || 'Dr. Sarah';

  // Get profile image from API or use default
  const profileImage = basicInfo.image || "https://ui-avatars.com/api/?name=User&background=3B82F6&color=ffffff&size=256&bold=true&font-size=0.8";

  // Get email from API or use default
  const email = basicInfo.email || 'sarah.johnson@example.com';

  // Handle search toggle
  const handleSearchToggle = () => {
    if (!isSearchOpen) {
      setIsSearchOpen(true);
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    } else {
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle search clear
  const handleClearSearch = () => {
    setSearchQuery('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Handle notifications dropdown animations
  const handleNotificationsDropdownToggle = () => {
    if (isNotificationsDropdownOpen) {
      setNotificationAnimation('slide-up');
      setTimeout(() => {
        setIsNotificationsDropdownOpen(false);
        setNotificationAnimation('');
      }, 300);
    } else {
      setIsNotificationsDropdownOpen(true);
      setNotificationAnimation('slide-down');
    }
  };

  // Handle profile dropdown animations
  const handleProfileDropdownToggle = () => {
    if (isProfileDropdownOpen) {
      setDropdownAnimation('slide-up');
      setTimeout(() => {
        setIsProfileDropdownOpen(false);
        setDropdownAnimation('');
      }, 300);
    } else {
      setIsProfileDropdownOpen(true);
      setDropdownAnimation('slide-down');
    }
  };

  // Handle navigation to Profile page
  const handleProfileClick = () => {
    setIsProfileDropdownOpen(false);
    navigate('/profile');
  };

  // Handle navigation to Profile page for settings (same page)
  const handleSettingsClick = () => {
    setIsProfileDropdownOpen(false);
    navigate('/profile');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close search dropdown
      if (isSearchOpen && !event.target.closest('.search-dropdown')) {
        setIsSearchOpen(false);
        setSearchQuery('');
      }
      
      // Close profile dropdown
      if (isProfileDropdownOpen && !event.target.closest('.profile-dropdown')) {
        setDropdownAnimation('slide-up');
        setTimeout(() => {
          setIsProfileDropdownOpen(false);
          setDropdownAnimation('');
        }, 300);
      }
      
      // Close notifications dropdown
      if (isNotificationsDropdownOpen && !event.target.closest('.notifications-dropdown')) {
        setNotificationAnimation('slide-up');
        setTimeout(() => {
          setIsNotificationsDropdownOpen(false);
          setNotificationAnimation('');
        }, 300);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchOpen, isProfileDropdownOpen, isNotificationsDropdownOpen]);

  // Handle logout confirmation
  const handleLogoutClick = () => {
    setIsProfileDropdownOpen(false);
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  const cancelLogout = () => {
    setShowLogoutConfirm(false);
  };

  return (
    <header className="sticky top-0 z-40 flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between h-16 sm:h-20 px-2 sm:px-6 lg:px-3">
        {/* Left side - Mobile menu button and title */}
        <div className="flex items-center space-x-3 sm:space-x-4">
          {/* Enhanced Mobile menu button - Only show on md and smaller screens */}
          <button
            type="button"
            className="md:hidden p-2 sm:p-3 rounded-2xl text-gray-500 hover:text-green-600 hover:bg-green-50 transition-all duration-300 ease-out transform hover:scale-110 hover:rotate-180"
            onClick={() => setSidebarOpen(true)}
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          {/* Enhanced Title with responsive text sizing */}
          <div className="flex flex-col">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold bg-gradient-to-r from-green-600 to-green-700 bg-clip-text text-transparent leading-tight">
              Instructor Dashboard
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5 sm:mt-1 xs:block">
              {basicInfoLoading ? 'Loading...' : basicInfoError ? 'Welcome back!' : `Welcome back, ${firstName}!`}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3 sm:space-x-4 lg:space-x-6">
          {/* <div className="search-dropdown relative">
            <div className={`items-center space-x-3 bg-gray-50 rounded-2xl px-3 py-2 sm:px-4 sm:py-2 hover:bg-green-50 transition-all duration-300 ease-out group ${
              isSearchOpen 
                ? 'flex absolute left-1/2 transform -translate-x-1/2 top-16 sm:top-20 w-11/12 sm:w-80 z-50 shadow-lg' 
                : 'hidden'
            }`}>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-green-500 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              
              <div className="relative flex-1">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="bg-transparent border-none focus:outline-none focus:ring-0 text-sm text-gray-600 placeholder-gray-400 w-full pr-8"
                />
                
                {searchQuery && (
                  <button 
                    onClick={handleClearSearch}
                    className="absolute right-0 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              
              <button 
                onClick={handleSearchToggle}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {isSearchOpen && searchQuery && (
              <div className="absolute left-1/2 transform -translate-x-1/2 top-24 sm:top-28 w-11/12 sm:w-80 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">Search Results</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {searchLoading 
                      ? 'Searching...' 
                      : searchError 
                        ? searchError?.data?.message || 'Search failed'
                        : searchData?.data 
                          ? 'Found results' 
                          : 'No results found'
                    }
                  </p>
                </div>
                
                <div className="max-h-80 overflow-y-auto py-1">
                  {searchLoading ? (
                    <div className="px-4 py-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Searching...</p>
                    </div>
                  ) : searchError ? (
                    <div className="px-4 py-6 text-center">
                      <svg className="w-12 h-12 text-red-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-medium text-gray-900">Search Failed</p>
                      <p className="text-xs text-gray-500 mt-1">Please try different keywords</p>
                    </div>
                  ) : searchData?.data && (
                    <>
                      {searchData.data.courses?.length > 0 && searchData.data.courses.map(course => (
                        <div key={course.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-2 border-l-green-500">
                          <p className="text-sm font-medium text-gray-900">{course.title}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{course.description}</p>
                        </div>
                      ))}
                      
                      {searchData.data.users?.length > 0 && searchData.data.users.map(user => (
                        <div key={user.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-2 border-l-blue-500">
                          <p className="text-sm font-medium text-gray-900">{user.fullname}</p>
                          <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                        </div>
                      ))}
                      
                      {searchData.data.categories?.length > 0 && searchData.data.categories.map(category => (
                        <div key={category.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-l-2 border-l-purple-500">
                          <p className="text-sm font-medium text-gray-900">{category.name}</p>
                        </div>
                      ))}
                      
                      {!searchData.data.courses?.length && !searchData.data.users?.length && !searchData.data.categories?.length && (
                        <div className="px-4 py-6 text-center">
                          <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          <p className="text-sm font-medium text-gray-900">No results found</p>
                          <p className="text-xs text-gray-500 mt-1">Try different keywords</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            <button 
              onClick={handleSearchToggle}
              className="p-2 sm:p-3 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-2xl transition-all duration-300 ease-out transform hover:scale-110"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div> */}

          {/* <div className="relative notifications-dropdown">
            <button 
              onClick={handleNotificationsDropdownToggle}
              className="relative p-2 sm:p-3 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-2xl transition-all duration-300 ease-out transform hover:scale-110 group"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM10.5 3.75a6 6 0 0 0-6 6v2.25l-2.47 2.47a.75.75 0 0 0 .53 1.28h15.88a.75.75 0 0 0 .53-1.28L16.5 12V9.75a6 6 0 0 0-6-6z" />
              </svg>
              
              {unreadCount > 0 && (
                <span className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold rounded-full animate-pulse shadow-lg">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
              
              <span className="absolute -top-1 -right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            </button>

            {isNotificationsDropdownOpen && (
              <div className={`absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 notification-dropdown-menu ${notificationAnimation}`}>
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">Notifications</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {unreadCount > 0 
                      ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
                      : 'No new notifications'
                    }
                  </p>
                </div>
                
                <div className="max-h-96 overflow-y-auto py-1">
                  {notificationsLoading ? (
                    <div className="px-4 py-6 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
                      <p className="text-sm text-gray-500 mt-2">Loading notifications...</p>
                    </div>
                  ) : notificationsError ? (
                    <div className="px-4 py-6 text-center">
                      <svg className="w-12 h-12 text-red-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm font-medium text-gray-900">Failed to load notifications</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {notificationsError?.data?.message || 'Please try again'}
                      </p>
                      <button
                        onClick={() => refetchNotifications()}
                        className="mt-3 text-sm text-green-600 hover:text-green-700 font-medium hover:bg-green-50 px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Retry
                      </button>
                    </div>
                  ) : notifications.length === 0 ? (
                    
                    <div className="px-4 py-6 text-center">
                      <svg className="w-12 h-12 text-gray-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                      </svg>
                      <p className="text-sm font-medium text-gray-900">No notifications</p>
                      <p className="text-xs text-gray-500 mt-1">You're all caught up!</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`flex items-start px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer border-l-2 ${
                          notification.isRead 
                            ? 'border-l-transparent opacity-75' 
                            : 'border-l-green-500 bg-green-50/30'
                        }`}
                      >
                        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                          notification.isRead ? 'bg-gray-100' : 'bg-green-100'
                        }`}>
                          <svg className={`w-4 h-4 ${
                            notification.isRead ? 'text-gray-400' : 'text-green-500'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${
                            notification.isRead ? 'text-gray-600' : 'text-gray-900 font-medium'
                          }`}>
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {notification.formattedDate || 'Recently'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="px-4 py-3 border-t border-gray-100">
                  <button
                    onClick={() => refetchNotifications()}
                    className="w-full text-center text-sm text-green-600 hover:text-green-700 font-medium hover:bg-green-50 py-2 rounded-lg transition-colors"
                  >
                    Refresh Notifications
                  </button>
                </div>
              </div>
            )}
          </div> */}

          <div className="relative profile-dropdown">
            <button 
              onClick={handleProfileDropdownToggle}
              className="flex items-center space-x-2 sm:space-x-3 text-sm rounded-2xl focus:outline-none p-1 sm:p-2 hover:bg-green-50 transition-all duration-300 ease-out transform hover:scale-105"
            >
              <div className="relative">
                {basicInfoLoading ? (
                  <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-2xl border-2 border-gray-200 bg-gray-100 animate-pulse"></div>
                ) : (
                  <>
                    <img
                      className="h-8 w-8 sm:h-10 sm:w-10 rounded-2xl border-2 border-green-300 shadow-lg group-hover:border-green-400 transition-all duration-300 ease-out"
                      src={profileImage}
                      alt={fullName}
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80";
                      }}
                    />
                    <div className="absolute -bottom-1 -right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-500 border-2 border-white rounded-full animate-pulse"></div>
                  </>
                )}
              </div>
              
              {/* Profile info - hidden on mobile */}
              <div className="hidden lg:block text-left">
                <p className="text-sm font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-300">
                  {basicInfoLoading ? 'Loading...' : basicInfoError ? 'Dr. Sarah' : fullName}
                </p>
                <p className="text-xs text-green-600 font-medium">
                  Instructor
                </p>
              </div>

              {/* Dropdown arrow */}
              <svg className={`w-4 h-4 text-gray-400 group-hover:text-green-600 transition-colors duration-300 hidden lg:block transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className={`absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 dropdown-menu ${dropdownAnimation}`}>
                {/* User Info */}
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900">
                    {basicInfoLoading ? 'Loading...' : basicInfoError ? 'Dr. Sarah Johnson' : fullName}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {basicInfoLoading ? 'Loading...' : basicInfoError ? 'sarah.johnson@example.com' : email}
                  </p>
                </div>
                
                {/* Dropdown Items */}
                <div className="py-1">
                  <button
                    onClick={handleProfileClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition-all duration-200"
                  >
                    <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    My Profile
                  </button>
                  
                  <button
                    onClick={handleSettingsClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-600 transition-all duration-200"
                  >
                    <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Settings
                  </button>
                  
                  <div className="border-t border-gray-100 my-1"></div>
                  
                  {/* Logout Button */}
                  <button
                    onClick={handleLogoutClick}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-all duration-200 group"
                  >
                    <svg className="w-4 h-4 mr-3 text-red-500 group-hover:text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </div>
            )}

            {/* Simple dropdown indicator line */}
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0 group-hover:w-6 sm:group-hover:w-8 h-0.5 bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-300 ease-out"></div>
          </div>
        </div>
      </div>

      {/* Progress bar for page loading - Optional */}
      <div className="w-full h-1 bg-gradient-to-r from-green-400 to-green-600 opacity-50 animate-pulse"></div>

      {/* Logout Confirmation Popup */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-6 sm:p-8 mx-4 max-w-sm w-full shadow-2xl transform animate-scale-in">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>

            {/* Popup Content */}
            <div className="text-center">
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                Are you sure?
              </h3>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                You will be logged out of your account. Any unsaved changes will be lost.
              </p>

              {/* Action Buttons */}
              <div className="flex space-x-3 sm:space-x-4">
                <button
                  onClick={cancelLogout}
                  className="flex-1 px-4 py-2 sm:py-3 text-sm sm:text-base font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmLogout}
                  className="flex-1 px-4 py-2 sm:py-3 text-sm sm:text-base font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { 
            opacity: 0; 
          }
          to { 
            opacity: 1; 
          }
        }
        
        @keyframes scale-in {
          from { 
            opacity: 0; 
            transform: scale(0.9); 
          }
          to { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        
        @keyframes slide-down {
          from { 
            opacity: 0; 
            transform: translateY(-10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes slide-up {
          from { 
            opacity: 1; 
            transform: translateY(0); 
          }
          to { 
            opacity: 0; 
            transform: translateY(-10px); 
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.3s ease-out both;
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out both;
        }
        
        .dropdown-menu.slide-down {
          animation: slide-down 0.3s ease-out both;
        }
        
        .dropdown-menu.slide-up {
          animation: slide-up 0.3s ease-out both;
        }
        
        .notification-dropdown-menu.slide-down {
          animation: slide-down 0.3s ease-out both;
        }
        
        .notification-dropdown-menu.slide-up {
          animation: slide-up 0.3s ease-out both;
        }
      `}</style>
    </header>
  );
};

export default Navbar;